import { useEffect, useState } from 'react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { AsyncMultiSelect } from '@/shared/ui/filters'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import { listMobilizers } from '@/features/mobilizers/api'
import { roleLabel } from '@/features/mobilizers/vocab'
import type { SurveyBackCheckDto } from '@/shared/api/types'
import { assignBackCheck, recordBackCheckOutcome } from './api'
import { CODE_LIST_UNAVAILABLE, useCodes } from './useVocabulary'
import { Pill, backCheckOutcomeLabel } from './vocab'

/**
 * The one-line meanings for `BackCheckOutcome`.
 *
 * The CODES come from `useCodes('backCheckOutcome')` and the English from
 * `vocab.tsx`. What is kept here is the sentence each finding needs, keyed by
 * code so it travels with whatever the server offers. `UNREACHABLE` and `NOT_CONDUCTED`
 * are routinely confused — one is "we tried and could not reach them", the other
 * is "we never tried" — and a supervisor reading a column of bare codes cannot
 * tell a failed verification from an unstarted one. A sixth outcome added
 * server-side appears in the radio list immediately, labelled with its raw code
 * and carrying no sentence, which is a visible prompt to write it one.
 */
const OUTCOME_MEANING: Record<string, string> = {
  VERIFIED: 'The respondent confirmed the interview happened and their answers match.',
  PARTIAL_MISMATCH: 'The interview happened, but some answers disagree with what the '
    + 'respondent says now.',
  FABRICATED: 'The respondent says this interview never took place.',
  UNREACHABLE: 'The verifier tried and could not reach the respondent. Nothing is confirmed '
    + 'either way — this is not evidence against the interviewer.',
  NOT_CONDUCTED: 'The back-check itself was never carried out. It says nothing about the '
    + 'interview; it says the verification did not happen.',
}

/**
 * Assigning a back-check, and recording what the verifier found.
 *
 * ## `FABRICATED` is a bulk action wearing a single-row costume
 *
 * Every other outcome ends where it started — one back-check, one interview.
 * `FABRICATED` does not: the server flags every other interview that interviewer
 * submitted on that poll, pulling all of them out of the estimates at once and
 * putting a concern on their roster record. That consequence is stated **before**
 * the button is pressed, not in a toast afterwards, because a person who learns
 * it afterwards has already done it. Nothing here is a confirmation step for its
 * own sake — the sentence is what makes the click informed.
 *
 * They are FLAGGED and not rejected: some of that interviewer's work may well be
 * real, and a moderator still decides one interview at a time.
 *
 * ## The note
 *
 * The server refuses any non-`VERIFIED` outcome without one. The button is still
 * offered without a note — rule 3 — because the refusal explains what the note is
 * for far better than a disabled control can: it is what the interviewer gets to
 * answer when this finding is put to them.
 *
 * ## The picker
 *
 * Same approach as `AssignDialog`: `listMobilizers` filtered to `ACTIVE`, through
 * `AsyncMultiSelect`, single choice via `slice(-1)`. There is no standalone
 * mobilizer picker component to share, and a second list endpoint would be a
 * second definition of who is active.
 */
export function BackCheckDialog({ open, backCheck, subjectLabel, onClose, onSaved }: {
  open: boolean
  backCheck: SurveyBackCheckDto | null
  /** Who conducted the interview under check, from the queue. */
  subjectLabel?: string | null
  onClose: () => void
  onSaved: () => void
}) {
  const outcomes = useCodes('backCheckOutcome')
  const [picked, setPicked] = useState<{ value: string; label: string }[]>([])
  const [outcomeCode, setOutcomeCode] = useState('')
  const [mismatchCount, setMismatchCount] = useState('0')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState<'assign' | 'outcome' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setPicked([])
    setOutcomeCode('')
    setMismatchCount('0')
    setNote('')
    setError(null)
    setDone(null)
  }, [open, backCheck?.id])

  if (!backCheck) return null

  const needsNote = outcomeCode !== '' && outcomeCode !== 'VERIFIED'
  const completed = backCheck.statusCode === 'COMPLETED'

  async function run(kind: 'assign' | 'outcome', call: () => Promise<unknown>, said: string) {
    setBusy(kind)
    setError(null)
    setDone(null)
    try {
      await call()
      setDone(said)
      onSaved()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="md"
      title="Back-check"
      description="Re-contacting the respondent. Detection without verification is a list of complaints."
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Close
          </button>
          {/*
            Offered with no outcome picked and with no note written. The server
            names which of the two it was, and its sentence about the note is the
            explanation the moderator needs; a disabled button is a dead end.
          */}
          <button
            type="button"
            className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={busy !== null}
            onClick={() => void run('outcome',
              () => recordBackCheckOutcome(backCheck.id, {
                outcomeCode,
                // `mismatchCount` is an `int` @Min(0), not an Integer — there is
                // no "unknown" to send, so an unparseable box is 0 answers
                // disagreed and the note carries the nuance.
                mismatchCount: Number.isFinite(Number(mismatchCount)) ? Number(mismatchCount) : 0,
                note: note.trim() === '' ? undefined : note.trim(),
              }),
              outcomeCode === 'FABRICATED'
                ? 'Recorded as FABRICATED. Every other interview this person submitted on this '
                  + 'poll has been flagged and is out of the estimates until each is decided.'
                : `Recorded as ${outcomeCode}.`)}
          >
            {busy === 'outcome' ? 'Recording…' : 'Record outcome'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />
        {done && <Note tone="good" onDismiss={() => setDone(null)}>{done}</Note>}

        <div className="flex flex-wrap items-center gap-2 text-erp-sm">
          <Pill>{backCheck.statusCode}</Pill>
          {backCheck.outcomeCode && <Pill>{backCheck.outcomeCode}</Pill>}
          <span className="text-muted-foreground">
            {subjectLabel ? `Interview by ${subjectLabel}.` : 'One sampled interview.'}
          </span>
          {backCheck.conductedAt && (
            <span className="text-muted-foreground">
              Conducted {fmtDateTime(backCheck.conductedAt)}.
            </span>
          )}
        </div>

        {completed && (
          <Note tone="info">
            A finding is already recorded against this back-check. Reassigning it is refused —
            it would put somebody else's name on a finding they did not make — and recording a
            second outcome overwrites the first, which is a thing to do only when the first was
            entered in error.
          </Note>
        )}

        {backCheck.note && (
          <Note tone="info">
            <span className="font-medium">On record: </span>{backCheck.note}
          </Note>
        )}

        <Field
          label="Who is doing the verification"
          hint="Active field staff only. Assigning is a separate write from recording — it is how a supervisor hands the call out before anybody has called."
        >
          <AsyncMultiSelect
            label="Verifier"
            selected={picked}
            // Single choice through a multi-select: `slice(-1)` keeps the last
            // click. `AssignBackCheckRequest.assignedTo` is one UUID.
            onChange={(next) => setPicked(next.slice(-1))}
            placeholder="Search by name"
            emptyLabel="Nobody chosen"
            search={async (term) => {
              const page = await listMobilizers({ q: term, status: 'ACTIVE' }, undefined, 25)
              return page.content.map((m) => ({
                value: m.id,
                label: m.fullName,
                sub: `${roleLabel(m.roleCode)} · ${m.primaryUnitName ?? 'no unit'}`,
              }))
            }}
          />
        </Field>

        {/*
          Outside the `Field`, which wraps its children in a <label>: a <button>
          inside a label steals the click to the label's control, and the picker's
          search box is what it would have landed in.
        */}
        <div className="flex flex-wrap items-center gap-2 -mt-1">
          <button
            type="button"
            className="erp-btn h-8 text-erp-xs"
            disabled={busy !== null}
            onClick={() => void run('assign',
              () => assignBackCheck(backCheck.id, picked[0]?.value ?? null),
              picked[0] ? `Assigned to ${picked[0].label}.`
                : 'Unassigned. The back-check is back in the pending pool.')}
          >
            {busy === 'assign' ? 'Saving…' : picked[0] ? 'Assign' : 'Leave unassigned'}
          </button>
          <span className="text-erp-xs text-muted-foreground min-w-0 flex-1">
            {picked[0]
              ? `${picked[0].label} will be named as the verifier on whatever is recorded.`
              : 'With nobody chosen this clears the assignee and returns it to PENDING.'}
          </span>
        </div>

        {/*
          A fieldset rather than a `Field`: five radios need one group name and one
          legend, and `Field`'s single wrapping <label> cannot nest the five that
          give each option its own hit area.
        */}
        <fieldset className="space-y-1.5">
          <legend className="text-erp-xs font-medium text-muted-foreground mb-1.5">
            What the verifier found<span className="text-red-600 ml-0.5">*</span>
          </legend>
          {outcomes.loading && (
            <p className="text-erp-xs text-muted-foreground">Loading the finding list…</p>
          )}
          {outcomes.unavailable && (
            // Beside the empty group in visible text. An unloaded list of findings
            // and a set of findings with nothing in it look identical, and the
            // second reading would have somebody conclude a back-check cannot be
            // recorded at all.
            <div className="space-y-1.5">
              <p className="text-erp-sm text-red-700">{CODE_LIST_UNAVAILABLE}</p>
              <button type="button" className="erp-btn-ghost h-7 text-erp-xs"
                onClick={() => outcomes.reload()}>
                Try again
              </button>
            </div>
          )}
          {outcomes.codes.map((code) => (
            <label
              key={code}
              className="flex items-start gap-2 rounded-md border border-border px-2.5 py-2 cursor-pointer hover:bg-muted/40"
            >
              <input
                type="radio"
                name="back-check-outcome"
                className="mt-1 shrink-0"
                checked={outcomeCode === code}
                onChange={() => setOutcomeCode(code)}
              />
              <span className="min-w-0">
                <span className={`text-erp-sm block ${
                  code === 'FABRICATED' ? 'text-rose-600 font-medium' : ''}`}>
                  {backCheckOutcomeLabel(code)} <span className="font-mono text-erp-xs text-muted-foreground">
                    {code}
                  </span>
                </span>
                {/* A finding with no sentence written for it is still offered —
                    the server calls it valid, and hiding it would make a legal
                    verdict unrecordable. */}
                {OUTCOME_MEANING[code] && (
                  <span className="text-erp-xs text-muted-foreground block">
                    {OUTCOME_MEANING[code]}
                  </span>
                )}
              </span>
            </label>
          ))}
          <p className="text-erp-xs text-muted-foreground">
            The meanings are not interchangeable — read them. Unreachable and Not conducted are
            the pair that get confused, and they say opposite things about who did what.
          </p>
        </fieldset>

        {/*
          The whole reason this dialog exists as a dialog rather than a dropdown
          in a table row. Stated on selection, before the button is anywhere near
          the pointer.
        */}
        {outcomeCode === 'FABRICATED' && (
          <Note tone="bad">
            <strong className="font-semibold">This reaches past this one interview.</strong>{' '}
            Recording FABRICATED tells the server the work did not happen, and it flags{' '}
            <strong className="font-semibold">every other interview
            {subjectLabel ? ` ${subjectLabel}` : ' this interviewer'} submitted on this poll</strong>{' '}
            — all of them leave the estimates at once, and a concern goes on their roster
            record. They are flagged and not rejected, because some of that work may be real
            and a moderator still decides one at a time. There is no single click that undoes
            this.
          </Note>
        )}

        <div className="max-w-[14rem]">
          <Field
            label="Answers that disagreed"
            hint="How many re-asked answers did not match. Zero is meaningful on VERIFIED and on FABRICATED — on the second, nothing was re-asked."
          >
            <input
              className="erp-input h-9 w-full text-right tabular-nums"
              type="number"
              min={0}
              value={mismatchCount}
              onChange={(e) => setMismatchCount(e.target.value)}
            />
          </Field>
        </div>

        <Field
          label="Note"
          required={needsNote}
          hint="Up to 2000 characters. What the respondent actually said, in their terms."
        >
          <textarea
            className="erp-input w-full min-h-[96px] py-2"
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Respondent confirmed the visit but says she was never asked about water."
          />
        </Field>

        {needsNote && note.trim() === '' && (
          <Note tone="info">
            The server refuses a {outcomeCode} finding with no note. It is not paperwork: this
            finding is about somebody's work, and the note is what that person gets to answer
            when it is put to them. The button is still live — press it and the refusal will
            say the same thing in the server's own words.
          </Note>
        )}
      </div>
    </Modal>
  )
}
